import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Subject } from 'rxjs'

import { SessionExpired } from '@/domain/events/SessionExpired'
import { UserChartChanged } from '@/domain/events/UserChartChanged'
import { UserChartSynced } from '@/domain/events/UserChartSynced'
import { UserGeometryChanged } from '@/domain/events/UserGeometryChanged'
import { UserGeometrySynced } from '@/domain/events/UserGeometrySynced'
import { UserWaypointChanged } from '@/domain/events/UserWaypointChanged'

import { queryKeys } from './queryKeys'

/**
 * Eventos de domínio → invalidação de query, num ponto único.
 *
 * Antes essa fiação vivia dentro de `useMyContentData`, que assinava os cinco Subjects por conta
 * própria. Centralizar aqui é o que permite qualquer tela reagir a uma mutação feita em outro lugar
 * (mapa, import, sync) sem cada uma repetir as subscriptions.
 *
 * REGRA: mutation que já emite um Subject **não** chama `invalidateQueries` — senão são dois
 * refetches por mutação.
 *
 * `UserWaypointChanged` NÃO invalida `adhp.*` de propósito: o `AeroInfoRepository` já assina esse
 * evento no construtor e recarrega os waypoints custom com `void _loadCustomWaypoints()`, sem
 * aguardar. Invalidar uma query de busca no mesmo tick faria ela refetchar ANTES do repositório
 * terminar, servindo dado velho. E não é necessário: a busca de ADHP é feita em memória, não em rede.
 */
const EVENT_INVALIDATIONS: ReadonlyArray<{ event: Subject<void>; keys: readonly unknown[][] }> = [
  { event: UserChartChanged, keys: [[...queryKeys.userContent.charts()]] },
  { event: UserChartSynced, keys: [[...queryKeys.userContent.charts()]] },
  { event: UserGeometryChanged, keys: [[...queryKeys.userContent.geometries()]] },
  { event: UserGeometrySynced, keys: [[...queryKeys.userContent.geometries()]] },
  { event: UserWaypointChanged, keys: [[...queryKeys.userContent.waypoints()]] }
]

export function useDomainEventInvalidation(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const subscriptions = EVENT_INVALIDATIONS.map(({ event, keys }) =>
      event.subscribe(() => {
        keys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }))
      })
    )

    // Sessão morta: cancela o que está em voo para não gerar uma cascata de 401/403 por baixo do
    // modal de sessão expirada. NÃO limpa o cache — isso apagaria as telas ainda visíveis.
    subscriptions.push(SessionExpired.subscribe(() => void queryClient.cancelQueries()))

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [queryClient])
}
